import classNames from '@/utils/classnames';

/**
 * Compositional table primitives. Standardizes header styling, row borders,
 * hover, and cell padding using semantic tokens. Wrap in a `Card`
 * (`padding="none"`) + `overflow-hidden` for a framed table.
 */
export function Table({ className, ...props }: React.TableHTMLAttributes<HTMLTableElement>) {
  return <table className={classNames('w-full border-collapse', className)} {...props} />;
}

export function TableHead({ className, ...props }: React.HTMLAttributes<HTMLTableSectionElement>) {
  return <thead className={classNames('border-b border-line bg-surface-3/50', className)} {...props} />;
}

export function TableBody({ className, ...props }: React.HTMLAttributes<HTMLTableSectionElement>) {
  return <tbody className={className} {...props} />;
}

interface TableRowProps extends React.HTMLAttributes<HTMLTableRowElement> {
  clickable?: boolean;
}

export function TableRow({ className, clickable, ...props }: TableRowProps) {
  return (
    <tr
      className={classNames(
        'border-b border-line last:border-b-0',
        clickable && 'cursor-pointer transition-colors hover:bg-surface-3/50',
        className,
      )}
      {...props}
    />
  );
}

export function TableHeaderCell({ className, ...props }: React.ThHTMLAttributes<HTMLTableCellElement>) {
  return (
    <th
      className={classNames(
        'px-4 py-2.5 text-left text-2xs font-semibold uppercase tracking-wider text-fg-muted',
        className,
      )}
      {...props}
    />
  );
}

export function TableCell({ className, ...props }: React.TdHTMLAttributes<HTMLTableCellElement>) {
  return <td className={classNames('px-4 py-3 align-middle text-sm text-fg-secondary', className)} {...props} />;
}
